import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import {
  getTwelveHourTimeParts,
  isValid12HourClockTime,
  Meridiem,
  normalizeMeridiemTimeInput,
} from '../../../utils/timeUtils';

type TwelveHourTimeFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
};

const getTimeFieldParts = (value: string): { time: string; meridiem: Meridiem } => {
  const parts = getTwelveHourTimeParts(value);
  const currentMeridiem: Meridiem = new Date().getHours() >= 12 ? 'PM' : 'AM';

  return {
    time: parts.hour && parts.minute ? `${parts.hour}:${parts.minute}` : '',
    meridiem: parts.meridiem || currentMeridiem,
  };
};

const TwelveHourTimeField: React.FC<TwelveHourTimeFieldProps> = ({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  helperText,
}) => {
  const initialParts = getTimeFieldParts(value);
  const [timeValue, setTimeValue] = useState(initialParts.time);
  const [meridiem, setMeridiem] = useState<Meridiem>(initialParts.meridiem);
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (focused) return;

    const nextParts = getTimeFieldParts(value);
    if (!value || nextParts.time) {
      setTimeValue(nextParts.time);
      setMeridiem(nextParts.meridiem);
    }
  }, [focused, value]);

  const emitChange = (nextTime: string, nextMeridiem: Meridiem) => {
    onChange(nextTime ? `${nextTime} ${nextMeridiem}` : '');
  };

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextTime = normalizeMeridiemTimeInput(event.target.value);
    setTimeValue(nextTime);
    emitChange(nextTime, meridiem);
  };

  const handleMeridiemChange = (nextMeridiem: Meridiem) => {
    setMeridiem(nextMeridiem);
    emitChange(timeValue, nextMeridiem);
  };

  const showRequiredError = touched && required && !timeValue;
  const showFormatError = touched && Boolean(timeValue) && !isValid12HourClockTime(timeValue);
  const errorText = showRequiredError
    ? `${label} is required`
    : showFormatError
      ? 'Enter time like 10:00'
      : helperText;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          label={label}
          placeholder="hh:mm"
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={{ inputMode: 'numeric', maxLength: 5, pattern: '\\d{2}:\\d{2}' }}
          value={timeValue}
          onChange={handleTimeChange}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setTouched(true);
          }}
          disabled={disabled}
          required={required}
          error={showRequiredError || showFormatError}
          sx={{ flex: 1 }}
        />
        <FormControl size="small" disabled={disabled} sx={{ width: 110 }}>
          <InputLabel>AM/PM</InputLabel>
          <Select
            value={meridiem}
            label="AM/PM"
            onChange={(e) => handleMeridiemChange(e.target.value as Meridiem)}
          >
            <MenuItem value="AM">AM</MenuItem>
            <MenuItem value="PM">PM</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {errorText && <FormHelperText error={showRequiredError || showFormatError}>{errorText}</FormHelperText>}
    </Box>
  );
};

export default TwelveHourTimeField;
